import { useMemo } from 'react';
import { Button, Input, Modal } from '../../../../../common/_custom';
import { EMAIL, NAME, PHONE, TRIGGER_NOTIFICATION, JOB_ROLE } from '../../../../../utils/constants/companyLogin';
import styles from './EditUserModal.module.scss';
import CountryCodesDropdown, { CountryCodeData } from '../../../../Company/CompanyLogin/CountryCodesDropdown/CountryCodesDropdown';
import Dropdown from '../../../../../common/_custom/Dropdown';
import { DEFAULT_JOB_ROLES } from './AddUserModal';
import CheckBox from '../../../../../common/CheckBox/CheckBox';
import { useEditUserModal } from './useEditUserModal';
import { useVisibilityConfig } from '../../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../../utils/constants/visibilityKeys';
import { USER_TYPE_OPTIONS, buildRoleOptions, buildUserTypeOptions } from '../../ManageUserConstants';
import { isPnoUser } from '../../../../../utils/utils';
import { USER_TYPE } from '../../../../../utils/constants/user';

// Maps a raw userType enum to its external-category key used to look up the userType catalog.
// Lender only for now (Partner/Assessee are out of scope for the config-driven picker).
const categoryOf = (rawUserType?: string): string | null =>
    rawUserType === USER_TYPE.LENDER || rawUserType === USER_TYPE.LENDER_TEAM ? USER_TYPE.LENDER : null;

interface EditUserModalProps {
    isOpen: boolean;
    onClose: () => void;
    userData: any;
    allRowsData?: any;
    onUsersUpdate?: (updatedUsers: any[]) => void;
    countryCodes: CountryCodeData[];
    isCompanySide: boolean;
    allowUserTypeEdit?: boolean;
}

const EditUserModal = ({
    isOpen,
    onClose,
    userData,
    allRowsData,
    onUsersUpdate,
    countryCodes,
    isCompanySide,
    allowUserTypeEdit = true,
}: EditUserModalProps) => {
    const { canWrite, roleCatalog, userTypeCatalog } = useVisibilityConfig();
    const canEditRole = canWrite(VISIBILITY_KEYS.USER_MANAGEMENT.EDIT_USER_ROLE);
    const canEditLenderUserType = canWrite(VISIBILITY_KEYS.LENDER_USER_TYPE_MANAGEMENT.EDIT_LENDER_USER_TYPE);
    const catalogResult = useMemo(() => (Object.keys(roleCatalog ?? {}).length > 0 ? buildRoleOptions(roleCatalog) : null), [roleCatalog]);
    const teamRoleOptions = catalogResult ? catalogResult.options : USER_TYPE_OPTIONS;
    const defaultTeamRoleId = catalogResult ? catalogResult.defaultId : undefined;
    // The investor org's own users (Admin / Team Member) are role-editable but identity-locked; every
    // other user type (lender/partner/borrower) is the opposite. Keyed off the raw userType enum the
    // backend now sends (the display label collides INVESTOR/ADMIN, so it can't be used here).
    // The getAllUsers/getAllUsersUnified listings send rawUserType; the org-drawer's embedded user
    // objects only carry userType, which holds the raw enum for lender users — fall back to it.
    const resolvedUserType = userData?.rawUserType ?? userData?.userType;
    const isInvestorSideUser = isPnoUser(resolvedUserType);

    // External (non-PNO) categories with a config-driven userType catalog get their own picker,
    // decoupled from the identity-lock flag. Currently Lender only.
    const externalCatalogResult = useMemo(() => {
        const category = categoryOf(resolvedUserType);
        const entries = category ? userTypeCatalog?.[category] : undefined;
        return entries && entries.length > 0 ? buildUserTypeOptions(entries) : null;
    }, [resolvedUserType, userTypeCatalog]);
    const isExternalTypeCategory = !isInvestorSideUser && !!externalCatalogResult;

    const effectiveOptions = isInvestorSideUser ? teamRoleOptions : externalCatalogResult?.options ?? [];
    const effectiveDefaultId = isInvestorSideUser ? defaultTeamRoleId : externalCatalogResult?.defaultId;

    // Internal-team picker stays gated by EDIT_USER_ROLE; the external (Lender) picker uses the
    // independent EDIT_LENDER_USER_TYPE permission so the two can be granted separately.
    const hasPermissionForPicker = isInvestorSideUser ? canEditRole : canEditLenderUserType;

    const {
        loading,
        formData,
        formErrors,
        handleFormChange,
        handleSave,
        handleConfirmDemotion,
        handleCancelDemotion,
        onClosedModal,
        isSaveDisabled,
        showDemotionConfirm,
        USER_ROLE_KEY,
    } = useEditUserModal({
        isOpen,
        userData,
        allRowsData,
        onUsersUpdate,
        countryCodes,
        onClose,
        isCompanySide,
        canEditRole,
        canEditLenderUserType,
        teamRoleOptions: effectiveOptions,
        defaultTeamRoleId: effectiveDefaultId,
        allowUserTypeEdit,
        isInvestorSideUser,
        isExternalTypeCategory,
    });

    if (showDemotionConfirm) {
        return (
            <Modal open={isOpen} onClose={onClosedModal} size="flexible" persistent style={{ padding: 0 }}>
                <div className={styles.ConfirmContainer}>
                    <div className={styles.ConfirmHeader}>
                        <div className={styles.ConfirmTitle}>Change Your Role?</div>
                        <div className={styles.ConfirmSubtitle}>
                            You are about to change your own role from Super Admin. You may lose administrative access. Are you sure?
                        </div>
                    </div>
                    <div className={styles.ConfirmFooter}>
                        <Button
                            text="Cancel"
                            variant="secondary"
                            onClick={handleCancelDemotion}
                            disabled={loading}
                            style={{ flex: 1, margin: 0 }}
                        />
                        <Button
                            text="Confirm"
                            variant="orange"
                            onClick={handleConfirmDemotion}
                            loading={loading}
                            disabled={loading}
                            style={{ flex: 1, margin: 0 }}
                        />
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal open={isOpen} onClose={onClosedModal} size="flexible" persistent style={{ padding: 0 }}>
            <div className={styles.ModalBody}>
                <h2>Edit User</h2>
                <Input
                    value={formData[NAME]}
                    onChange={(val) => handleFormChange(val, NAME)}
                    placeholder="Full Name"
                    style={{ marginTop: '30px' }}
                    isValid={!formErrors[NAME]}
                    errorMessage={formErrors[NAME]}
                    allowAllCharacters={true}
                    disabled={isInvestorSideUser}
                />
                <Input
                    value={formData[EMAIL]}
                    onChange={(val) => handleFormChange(val, EMAIL)}
                    placeholder="Email ID"
                    style={{ marginTop: '30px' }}
                    isValid={!formErrors[EMAIL]}
                    errorMessage={formErrors[EMAIL]}
                    allowAllCharacters={true}
                    disabled={isInvestorSideUser}
                />
                <Dropdown
                    optionsList={DEFAULT_JOB_ROLES}
                    selectedOption={formData[JOB_ROLE] || null}
                    itemClickHandler={(option) => {
                        handleFormChange(option, JOB_ROLE);
                    }}
                    label="Job Role"
                    placeholder="Job Role"
                    enableSearch={true}
                    hideOtherOptionWhenNoMatch={true}
                    customOption={{
                        enabled: true,
                        text: 'Add Other Job Role',
                        placeholder: 'Enter job role...',
                        maxLength: 50,
                    }}
                    style={{ marginTop: '30px' }}
                />
                {allowUserTypeEdit && hasPermissionForPicker && effectiveOptions.length > 0 && (
                    <Dropdown
                        optionsList={effectiveOptions}
                        selectedOption={formData[USER_ROLE_KEY] || null}
                        itemClickHandler={(option) => handleFormChange(option, USER_ROLE_KEY)}
                        label="User Type"
                        placeholder="User Type"
                        style={{ marginTop: '1.875rem' }}
                        hideClearIcon
                    />
                )}
                <div className={styles.Row}>
                    <Input
                        placeholder="Phone Number"
                        value={formData[PHONE]}
                        onChange={(value) => {
                            if (/^\d*$/.test(value) && value.length <= 10) {
                                handleFormChange(value, PHONE);
                            }
                        }}
                        style={{ marginTop: '0', width: '100%' }}
                        type="number"
                        isValid={!formErrors[PHONE]}
                        errorMessage={formErrors[PHONE]}
                        disabled={isInvestorSideUser}
                    />
                </div>
                <CheckBox
                    label={'Send invite on email'}
                    checked={formData[TRIGGER_NOTIFICATION]}
                    onCheck={(val) => handleFormChange(val, TRIGGER_NOTIFICATION)}
                    variant={'tertiary'}
                    style={{ padding: '1.5rem 0 0rem 0.25rem' }}
                />
            </div>
            <div className={styles.Bottom}>
                <Button
                    onClick={handleSave}
                    text={'Save'}
                    variant="purple"
                    loading={loading}
                    style={{ width: '100%' }}
                    disabled={isSaveDisabled}
                />
            </div>
        </Modal>
    );
};

export default EditUserModal;
